import {
  collection,
  doc,
  getDocs,
  writeBatch,
  serverTimestamp,
  query,
  orderBy,
  limit as firestoreLimit,
  getDoc,
  setDoc,
} from 'firebase/firestore';
import { db, auth } from '../firebase/firebase';

const AUDIT_SUBCOLLECTION = 'audit_log';

// Fields to skip when computing diffs for orders
const SKIP_FIELDS = ['createdAt', 'store_id', 'customerId', 'id'];

/**
 * Compute field-level diff between current document data and updates.
 * Returns only changed fields as { old, new } pairs.
 */
export function computeOrderDiff(current, updates) {
  const changes = {};
  for (const key of Object.keys(updates)) {
    if (SKIP_FIELDS.includes(key)) continue;
    const oldVal = current[key] ?? null;
    const newVal = updates[key] ?? null;
    if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
      changes[key] = { old: oldVal, new: newVal };
    }
  }
  return changes;
}

/**
 * Get authenticated user info for audit attribution.
 * Returns { uid, email, role } from Firebase Auth currentUser.
 */
export function getAuditUser() {
  const user = auth.currentUser;
  if (!user) throw new Error('No hay sesion activa');
  return {
    uid: user.uid,
    email: user.email || 'unknown',
    role: 'staff',
  };
}

/**
 * Build an audit entry object (not yet written to Firestore).
 */
export function buildAuditEntry(action, changes = {}, metadata = null) {
  const user = getAuditUser();
  return {
    timestamp: serverTimestamp(),
    user_id: user.uid,
    user_email: user.email,
    user_role: user.role,
    action,
    changes,
    metadata,
  };
}

/**
 * Write a document update + audit entry in a single atomic batch.
 * @param {string} collectionName - 'breads' or 'cakes'
 * @param {string} docId - document ID
 * @param {object} updateData - fields to update on the document
 * @param {string} action - 'created' | 'updated' | 'deleted'
 * @param {object} changes - diff object for the audit entry
 * @param {object|null} metadata - optional metadata
 * @returns {Promise<void>}
 */
export async function writeWithAudit(collectionName, docId, updateData, action, changes = {}, metadata = null) {
  const batch = writeBatch(db);
  const docRef = doc(db, collectionName, docId);
  const auditRef = doc(collection(docRef, AUDIT_SUBCOLLECTION));

  batch.update(docRef, updateData);
  batch.set(auditRef, buildAuditEntry(action, changes, metadata));

  await batch.commit();
}

/**
 * Write a soft-delete update + audit entry with full document snapshot.
 * @param {string} collectionName - 'breads' or 'cakes'
 * @param {string} docId - document ID
 * @param {object|null} existingSnap - optional pre-fetched DocumentSnapshot to avoid duplicate read
 * @returns {Promise<void>}
 */
export async function softDeleteWithAudit(collectionName, docId, existingSnap = null) {
  const user = getAuditUser();
  const docRef = doc(db, collectionName, docId);

  let docSnap = existingSnap;
  if (!docSnap) {
    docSnap = await getDoc(docRef);
  }

  if (!docSnap.exists()) {
    throw new Error('Documento no encontrado');
  }

  const fullSnapshot = docSnap.data();
  const batch = writeBatch(db);

  batch.update(docRef, {
    deleted: true,
    deletedAt: serverTimestamp(),
    deletedBy: user.email,
  });

  const auditRef = doc(collection(docRef, AUDIT_SUBCOLLECTION));
  batch.set(auditRef, buildAuditEntry('deleted', fullSnapshot, { soft_delete: true }));

  await batch.commit();
}

/**
 * Write audit entry for a newly created document (after addDoc).
 * @param {string} collectionName - 'breads' or 'cakes'
 * @param {string} docId - the new document ID from addDoc
 * @returns {Promise<void>}
 */
export async function writeCreateAudit(collectionName, docId) {
  const docRef = doc(db, collectionName, docId);
  const auditRef = doc(collection(docRef, AUDIT_SUBCOLLECTION));
  await setDoc(auditRef, buildAuditEntry('created', {}, null));
}

/**
 * Fetch audit log for an order.
 * @param {string} collectionName - 'breads' or 'cakes'
 * @param {string} docId - order document ID
 * @param {number} maxEntries - max entries to return (default 20)
 * @returns {Promise<Array>} audit entries sorted by timestamp DESC
 */
export async function getOrderAuditLog(collectionName, docId, maxEntries = 20) {
  const auditRef = collection(db, collectionName, docId, AUDIT_SUBCOLLECTION);
  const q = query(auditRef, orderBy('timestamp', 'desc'), firestoreLimit(maxEntries));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((docSnap) => {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      timestamp: data.timestamp,
      user_id: data.user_id,
      user_email: data.user_email,
      user_role: data.user_role,
      action: data.action,
      changes: data.changes ?? {},
      metadata: data.metadata ?? null,
    };
  });
}
